import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  keyframes,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import HandshakeIcon from "@mui/icons-material/Handshake";
import BusinessIcon from "@mui/icons-material/Business";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LanguageIcon from "@mui/icons-material/Language";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.haymestasarim.com/api";

const ENDPOINTS = {
  dashboard: "/bayilik/my-dashboard",
};

const MOBILE_BREAKPOINT = 930;

const BRAND = {
  blue: "#0B4D8B",
  blue2: "#1565C0",
  orange: "#F58220",
  dark: "#0F172A",
  ink: "#1E293B",
  muted: "#64748B",
  bg: "#F8FAFC",
  softBlue: "#EAF4FF",
  softOrange: "#FFF3E6",
  softGreen: "#ECFDF5",
  softRed: "#FEF2F2",
  softYellow: "#FFFBEB",
  danger: "#EF4444",
  border: "rgba(15, 23, 42, 0.10)",
  border2: "rgba(148, 163, 184, 0.24)",
};

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shine = keyframes`
  0% {
    background-position: -180% 0;
  }
  100% {
    background-position: 180% 0;
  }
`;

const floatSoft = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const TEXT = {
  tr: {
    pageTitle: "Bayilik Sistemi",
    pageSubtitle:
      "Referans satışlarınızı, müşteri kayıtlarını, sözleşme tarihlerini ve hak edişlerinizi tek ekrandan profesyonel şekilde takip edin.",
    refresh: "Yenile",
    refreshing: "Yenileniyor",
    exportCsv: "CSV İndir",
    lastUpdate: "Son güncelleme",
    never: "Henüz yok",
    totalRecords: "Toplam İşlem",
    activeContracts: "Aktif Sözleşme",
    upcomingRenewals: "Yaklaşan Yenileme",
    totalSalesAmount: "Toplam Satış",
    infoCommission: "Hak Ediş",
    searchPlaceholder: "Firma, referans, ürün, müşteri veya not ara...",
    recordsTitle: "Referans / Satış Bilgi Kayıtları",
    recordsSubtitle: "Admin panelinden girilen satış ve referans kayıtları.",
    reportsTitle: "Raporlar",
    reportsSubtitle:
      "En çok satış yapılan müşterileri ve en çok satılan ürün/paketleri takip edin.",
    customerReport: "En Çok Satış Yapılan Müşteriler",
    customerReportSub: "Hangi firma / hesap en çok aldı?",
    productReport: "Ürün / Paket Satış Raporu",
    productReportSub: "En çok hangi ürün veya paket satıldı?",
    noReport: "Rapor için yeterli veri yok.",
    noData: "Henüz kayıt yok.",
    noDataText:
      "Admin panelinden referans / satış işlemi girildiğinde burada görünecek.",
    company: "Firma",
    customerAccount: "Satış Yapılan Hesap",
    partner: "Referans / Satışçı",
    packageName: "Satılan Ürün / Paket",
    processType: "İşlem Tipi",
    saleAmount: "Satış Tutarı",
    commissionStep: "Hak Ediş",
    rate: "Oran",
    commissionAmount: "Hak Ediş",
    startDate: "Başlangıç",
    endDate: "Bitiş",
    agreementDate: "Anlaşma Tarihi",
    remainingDay: "Kalan Süre",
    note: "Not",
    noNote: "Not girilmemiş",
    sale: "Satış",
    demo: "Demo",
    renewal: "Yenileme",
    setup: "Kurulum",
    dateMissing: "Tarih yok",
    dayLeft: "gün kaldı",
    dayPassed: "gün geçti",
    errorTitle: "Veriler alınamadı",
    errorText:
      "Bağlantı veya backend endpoint kontrol edilmeli. Sayfayı yenileyip tekrar deneyebilirsiniz.",
    loadingTitle: "Bayilik bilgileri yükleniyor",
    loadingText:
      "Referans kayıtları, sözleşme tarihleri ve hak edişler hazırlanıyor.",
    rows: "kayıt",
    quantity: "Adet",
    sales: "Satışlar",
    salesListed: "kayıt listeleniyor",
    customerMissing: "Müşteri bilgisi yok",
    productMissing: "Ürün bilgisi yok",
  },
  en: {
    pageTitle: "Partner Sales System",
    pageSubtitle:
      "Track referral sales, customer records, contract dates and commissions professionally.",
    refresh: "Refresh",
    refreshing: "Refreshing",
    exportCsv: "Download CSV",
    lastUpdate: "Last update",
    never: "Never",
    totalRecords: "Total Records",
    activeContracts: "Active Contracts",
    upcomingRenewals: "Upcoming Renewals",
    totalSalesAmount: "Total Sales",
    infoCommission: "Commission",
    searchPlaceholder: "Search company, referral, product, customer or note...",
    recordsTitle: "Referral / Sales Records",
    recordsSubtitle: "Sales and referral records entered by admin.",
    reportsTitle: "Reports",
    reportsSubtitle: "Track top customers and top sold products/packages.",
    customerReport: "Top Customers",
    customerReportSub: "Which company / account bought the most?",
    productReport: "Product / Package Report",
    productReportSub: "Which product or package sold the most?",
    noReport: "Not enough data for report.",
    noData: "No records yet.",
    noDataText: "Records will appear here when admin enters them.",
    company: "Company",
    customerAccount: "Sold To",
    partner: "Referral / Seller",
    packageName: "Sold Product / Package",
    processType: "Process Type",
    saleAmount: "Sale Amount",
    commissionStep: "Commission",
    rate: "Rate",
    commissionAmount: "Commission",
    startDate: "Start",
    endDate: "End",
    agreementDate: "Agreement Date",
    remainingDay: "Time Left",
    note: "Note",
    noNote: "No note",
    sale: "Sale",
    demo: "Demo",
    renewal: "Renewal",
    setup: "Setup",
    dateMissing: "No date",
    dayLeft: "days left",
    dayPassed: "days passed",
    errorTitle: "Could not load data",
    errorText: "Check connection or backend endpoint and refresh the page.",
    loadingTitle: "Loading partner records",
    loadingText: "Referral records, contract dates and commissions are loading.",
    rows: "records",
    quantity: "Qty",
    sales: "Sales",
    salesListed: "records listed",
    customerMissing: "No customer",
    productMissing: "No product",
  },
  de: {
    pageTitle: "Partner-Vertriebssystem",
    pageSubtitle:
      "Empfehlungsverkäufe, Kunden, Vertragsdaten und Provisionen professionell verfolgen.",
    refresh: "Aktualisieren",
    refreshing: "Aktualisiert",
    exportCsv: "CSV herunterladen",
    lastUpdate: "Letzte Aktualisierung",
    never: "Noch nie",
    totalRecords: "Gesamt",
    activeContracts: "Aktive Verträge",
    upcomingRenewals: "Anstehende Verlängerungen",
    totalSalesAmount: "Gesamtverkauf",
    infoCommission: "Provision",
    searchPlaceholder: "Firma, Empfehlung, Produkt, Kunde oder Notiz suchen...",
    recordsTitle: "Empfehlungs-/Verkaufsdaten",
    recordsSubtitle: "Vom Admin eingetragene Verkaufs- und Empfehlungsdaten.",
    reportsTitle: "Berichte",
    reportsSubtitle: "Top-Kunden und meistverkaufte Produkte/Pakete verfolgen.",
    customerReport: "Top Kunden",
    customerReportSub: "Welche Firma / welches Konto kaufte am meisten?",
    productReport: "Produkt-/Paketbericht",
    productReportSub: "Welches Produkt oder Paket wurde am meisten verkauft?",
    noReport: "Nicht genug Daten für Bericht.",
    noData: "Noch keine Datensätze.",
    noDataText: "Datensätze erscheinen, wenn der Admin sie einträgt.",
    company: "Firma",
    customerAccount: "Verkauft an",
    partner: "Empfehlung / Verkäufer",
    packageName: "Verkauftes Produkt / Paket",
    processType: "Vorgang",
    saleAmount: "Verkaufsbetrag",
    commissionStep: "Provision",
    rate: "Satz",
    commissionAmount: "Provision",
    startDate: "Start",
    endDate: "Ende",
    agreementDate: "Vereinbarungsdatum",
    remainingDay: "Restzeit",
    note: "Notiz",
    noNote: "Keine Notiz",
    sale: "Verkauf",
    demo: "Demo",
    renewal: "Verlängerung",
    setup: "Einrichtung",
    dateMissing: "Kein Datum",
    dayLeft: "Tage übrig",
    dayPassed: "Tage vorbei",
    errorTitle: "Daten konnten nicht geladen werden",
    errorText: "Verbindung oder Backend-Endpunkt prüfen und Seite aktualisieren.",
    loadingTitle: "Partnerdaten werden geladen",
    loadingText: "Empfehlungen, Vertragsdaten und Provisionen werden geladen.",
    rows: "Datensätze",
    quantity: "Anzahl",
    sales: "Verkäufe",
    salesListed: "Datensätze angezeigt",
    customerMissing: "Kein Kunde",
    productMissing: "Kein Produkt",
  },
};

function getPanelLang() {
  const raw =
    localStorage.getItem("panel_dili") ||
    localStorage.getItem("app_lang") ||
    localStorage.getItem("lang") ||
    "tr";

  const lang = String(raw).slice(0, 2).toLowerCase();
  return ["tr", "en", "de"].includes(lang) ? lang : "tr";
}

function getLocale(lang) {
  if (lang === "en") return "en-US";
  if (lang === "de") return "de-DE";
  return "tr-TR";
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("user_token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("admin_token") ||
    ""
  );
}

function safeText(value, fallback = "-") {
  const v = String(value || "").trim();
  return v || fallback;
}

function safeLower(value) {
  return String(value || "").toLocaleLowerCase("tr-TR");
}

function parseNumber(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value)
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function normalizeCurrency(value) {
  const raw = String(value || "TRY").toUpperCase();
  if (raw === "TL") return "TRY";
  return raw;
}

function moneyText(value, currency = "TRY", lang = "tr") {
  const amount = parseNumber(value);

  const formatted = amount.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${formatted} ${normalizeCurrency(currency)}`;
}

function moneyTextShort(value, currency = "TRY", lang = "tr") {
  const amount = parseNumber(value);

  const formatted = amount.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return `${formatted} ${normalizeCurrency(currency)}`;
}

function percentText(value, lang = "tr") {
  const amount = parseNumber(value);

  return `${amount.toLocaleString(getLocale(lang), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

function dateText(value, lang = "tr") {
  if (!value) return "-";

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";

    return new Intl.DateTimeFormat(getLocale(lang), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return "-";
  }
}

function dateTimeText(value, lang = "tr") {
  if (!value) return "-";

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";

    return new Intl.DateTimeFormat(getLocale(lang), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "-";
  }
}

function getMonthSalesLabel(lang = "tr") {
  const now = new Date();

  const monthName = new Intl.DateTimeFormat(getLocale(lang), {
    month: "long",
  }).format(now);

  const cleanMonth =
    lang === "tr"
      ? monthName.charAt(0).toLocaleUpperCase("tr-TR") + monthName.slice(1)
      : monthName.charAt(0).toUpperCase() + monthName.slice(1);

  if (lang === "en") return `${cleanMonth} Sales`;
  if (lang === "de") return `${cleanMonth} Verkäufe`;

  return `${cleanMonth} Satış`;
}

function isCurrentMonthSale(row = {}) {
  const rawDate =
    row.anlasma_tarihi ||
    row.created_at ||
    row.sozlesme_baslangic ||
    row.raw?.anlasma_tarihi ||
    row.raw?.created_at;

  if (!rawDate) return false;

  const d = new Date(rawDate);
  const now = new Date();

  if (Number.isNaN(d.getTime())) return false;

  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function getDaysLeft(value) {

  if (!value) return null;

  const end = new Date(value);
  const now = new Date();

  if (Number.isNaN(end.getTime())) return null;

  end.setHours(23, 59, 59, 999);
  now.setHours(0, 0, 0, 0);

  const diff = end.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getProcessTypeText(value, TT) {
  const raw = String(value || "satis").toLowerCase();

  if (raw === "demo") return TT.demo;
  if (raw === "yenileme") return TT.renewal;
  if (raw === "kurulum") return TT.setup;

  return TT.sale;
}

function calculateCommission(amount, rate) {
  const sale = parseNumber(amount);
  const cleanRate = parseNumber(rate);
  return Math.round(((sale * cleanRate) / 100) * 100) / 100;
}

function getCommissionAmount(row) {
  const stored = parseNumber(row.hakedis_tutari);
  if (stored > 0) return stored;
  return calculateCommission(row.satis_tutari, row.hakedis_orani);
}

function getBuyerName(row = {}, TT = TEXT.tr) {
  return (
    row.musteri_adi ||
    row.musteri_name ||
    row.firma_adi ||
    row.company_name ||
    row.kurum_adi ||
    TT.customerMissing
  );
}

function getProductName(row = {}, TT = TEXT.tr) {
  return row.satilan_urun || row.package_name || row.paket || TT.productMissing;
}

function getRemainingChip(value, TT) {
  const days = getDaysLeft(value);

  if (days === null) {
    return {
      label: TT.dateMissing,
      bg: "#F1F5F9",
      color: "#475569",
    };
  }

  if (days < 0) {
    return {
      label: `${Math.abs(days)} ${TT.dayPassed}`,
      bg: "#FEF2F2",
      color: "#991B1B",
    };
  }

  if (days <= 15) {
    return {
      label: `${days} ${TT.dayLeft}`,
      bg: "#FEF2F2",
      color: "#991B1B",
    };
  }

  if (days <= 45) {
    return {
      label: `${days} ${TT.dayLeft}`,
      bg: "#FFF7ED",
      color: "#9A3412",
    };
  }

  return {
    label: `${days} ${TT.dayLeft}`,
    bg: "#ECFDF5",
    color: "#047857",
  };
}

function normalizeRecord(row = {}) {
  const saleAmount =
    row.satis_tutari ??
    row.sale_amount ??
    row.sales_amount ??
    row.total_amount ??
    row.amount ??
    0;

  const rate =
    row.hakedis_orani ??
    row.commission_rate ??
    row.hak_edis_orani ??
    row.rate ??
    0;

  const commission =
    row.hakedis_tutari ??
    row.commission_amount ??
    row.hak_edis ??
    row.hakedis ??
    calculateCommission(saleAmount, rate);

  return {
    id:
      row.id ??
      row.sale_id ??
      row.contract_id ??
      `${row.kurum_id || row.company_id || "record"}-${
        row.created_at || Date.now()
      }`,

    kurum_id: row.kurum_id ?? row.company_id ?? row.business_id ?? null,
    kurum_old_id: row.kurum_old_id ?? row.old_id ?? null,

    referans: row.referans ?? row.partner ?? row.seller ?? "",
    firma_adi:
      row.firma_adi ??
      row.company_name ??
      row.kurum_adi ??
      row.kurum_adı ??
      row.business_name ??
      row.ad ??
      "",

    satilan_urun:
      row.satilan_urun ??
      row.package_name ??
      row.paket ??
      row.plan_name ??
      row.product_name ??
      "",

    islem_tipi: row.islem_tipi ?? row.process_type ?? "satis",

    satis_tutari: parseNumber(saleAmount),
    currency: row.currency ?? row.para_birimi ?? "TRY",

    hakedis_kademe: row.hakedis_kademe ?? row.commission_step ?? "1",
    hakedis_1_oran: parseNumber(row.hakedis_1_oran ?? 0),
    hakedis_2_oran: parseNumber(row.hakedis_2_oran ?? 0),
    hakedis_3_oran: parseNumber(row.hakedis_3_oran ?? 0),
    hakedis_orani: parseNumber(rate),
    hakedis_tutari: parseNumber(commission),

    musteri_kurum_id: row.musteri_kurum_id ?? row.customer_company_id ?? null,
    musteri_old_id: row.musteri_old_id ?? row.customer_old_id ?? null,
    musteri_adi: row.musteri_adi ?? row.customer_name ?? "",
    musteri_email: row.musteri_email ?? row.customer_email ?? "",
    musteri_code: row.musteri_code ?? row.customer_code ?? "",
    musteri_short_code: row.musteri_short_code ?? row.customer_short_code ?? "",

    anlasma_tarihi:
      row.anlasma_tarihi ??
      row.sale_date ??
      row.satis_tarihi ??
      row.created_at ??
      "",

    sozlesme_baslangic:
      row.sozlesme_baslangic ??
      row.contract_start ??
      row.baslangic_tarihi ??
      row.start_date ??
      "",

    sozlesme_bitis:
      row.sozlesme_bitis ??
      row.contract_end ??
      row.bitis_tarihi ??
      row.end_date ??
      "",

    anlasma_notu:
      row.anlasma_notu ??
      row.note ??
      row.not ??
      row.description ??
      row.aciklama ??
      row.açıklama ??
      "",

    created_at: row.created_at ?? row.createdAt ?? "",
    raw: row,
  };
}

function normalizeDashboard(json = {}) {
  const data = json.data || json;

  const recordsRaw =
    data.islemler ||
    json.islemler ||
    data.records ||
    data.sales ||
    json.sales ||
    data.satislar ||
    [];

  const partnerRaw = data.partner || data.user || data.profile || {};

  const records = Array.isArray(recordsRaw)
    ? recordsRaw.map(normalizeRecord)
    : [];

  const summaryRaw = data.summary || json.summary || {};

  return {
    partner: {
      id: partnerRaw.id ?? partnerRaw.user_id ?? null,
      name:
        partnerRaw.name ??
        partnerRaw.full_name ??
        partnerRaw.ad_soyad ??
        partnerRaw.kurum_adi ??
        partnerRaw.email ??
        "",
      email: partnerRaw.email ?? partnerRaw.mail ?? "",
      code:
        partnerRaw.code ??
        partnerRaw.panel_code ??
        partnerRaw.kurum_code ??
        partnerRaw.short_code ??
        "",
    },
    records,
    summary: {
      toplam_islem:
        summaryRaw.toplam_islem ?? summaryRaw.total_records ?? records.length,
      aktif_sozlesme:
        summaryRaw.aktif_sozlesme ?? summaryRaw.active_contracts ?? 0,
      yaklasan_sozlesme:
        summaryRaw.yaklasan_sozlesme ?? summaryRaw.upcoming_renewals ?? 0,
    },
  };
}

async function apiJson(path, options = {}) {
  const token = getToken();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
      ...(options.headers || {}),
    },
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json.ok === false) {
    throw new Error(json.message || json.error || `HTTP ${res.status}`);
  }

  return json;
}

function csvValue(value) {
  const raw = String(value ?? "");
  return `"${raw.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const content = rows.map((r) => r.map(csvValue).join(";")).join("\n");

  const blob = new Blob(["\uFEFF" + content], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
}

function buildCsvRows(records, lang, TT) {
  return [
    [
      TT.partner,
      TT.company,
      TT.customerAccount,
      TT.processType,
      TT.packageName,
      TT.saleAmount,
      TT.commissionStep,
      TT.rate,
      TT.commissionAmount,
      TT.agreementDate,
      TT.startDate,
      TT.endDate,
      TT.remainingDay,
      TT.note,
    ],
    ...records.map((row) => {
      const chip = getRemainingChip(row.sozlesme_bitis, TT);

      return [
        row.referans,
        row.firma_adi,
        [
          safeText(row.musteri_adi || row.firma_adi, ""),
          row.musteri_email,
          row.musteri_code ? `Kod: ${row.musteri_code}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
        getProcessTypeText(row.islem_tipi, TT),
        row.satilan_urun,
        moneyText(row.satis_tutari, row.currency, lang),
        `${row.hakedis_kademe || "1"}. ${TT.commissionStep}`,
        percentText(row.hakedis_orani, lang),
        moneyText(getCommissionAmount(row), row.currency, lang),
        dateText(row.anlasma_tarihi, lang),
        dateText(row.sozlesme_baslangic, lang),
        dateText(row.sozlesme_bitis, lang),
        chip.label,
        row.anlasma_notu || "",
      ];
    }),
  ];
}

function buildReportRows(records, getKey, getSubText, TT) {
  const map = new Map();

  records.forEach((row) => {
    const rawKey = safeText(getKey(row), TT.noReport);
    const key = rawKey || TT.noReport;

    if (!map.has(key)) {
      map.set(key, {
        key,
        label: key,
        subText: getSubText ? safeText(getSubText(row), "") : "",
        count: 0,
        saleAmount: 0,
        commissionAmount: 0,
      });
    }

    const current = map.get(key);
    current.count += 1;
    current.saleAmount += parseNumber(row.satis_tutari);
    current.commissionAmount += getCommissionAmount(row);

    if (!current.subText && getSubText) {
      current.subText = safeText(getSubText(row), "");
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    if (b.saleAmount !== a.saleAmount) return b.saleAmount - a.saleAmount;
    return b.count - a.count;
  });
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    bgcolor: "#fff",
    fontWeight: 750,
    minWidth: 0,
    "& fieldset": {
      borderColor: BRAND.border,
    },
    "&:hover fieldset": {
      borderColor: alpha(BRAND.blue2, 0.34),
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND.blue2,
    },
  },
};

const selectSx = {
  borderRadius: 3,
  bgcolor: "#fff",
  fontWeight: 850,
  minWidth: 0,
  "& fieldset": {
    borderColor: BRAND.border,
  },
};

export default function BayilikSistemi() {
  const cardMode = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`);

  const [lang, setLang] = useState(getPanelLang);
  const [records, setRecords] = useState([]);
  const [partner, setPartner] = useState({});
  const [summary, setSummary] = useState({
    toplam_islem: 0,
    aktif_sozlesme: 0,
    yaklasan_sozlesme: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const TT = TEXT[lang] || TEXT.tr;

  useEffect(() => {
    localStorage.setItem("panel_dili", lang);
  }, [lang]);

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-bayilik-mobile-fix", "true");
    style.innerHTML = `
      html,
      body,
      #root {
        max-width: 100%;
      }

      .bayilik-root,
      .bayilik-root * {
        box-sizing: border-box;
      }

      .bayilik-root {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
      }

      .bayilik-root img,
      .bayilik-root svg,
      .bayilik-root canvas,
      .bayilik-root video {
        max-width: 100%;
      }

      .bayilik-root .MuiCard-root,
      .bayilik-root .MuiPaper-root,
      .bayilik-root .MuiStack-root,
      .bayilik-root .MuiGrid-root {
        min-width: 0;
        max-width: 100%;
      }

      .bayilik-root .MuiTypography-root {
        max-width: 100%;
      }

      .bayilik-root .MuiButton-root,
      .bayilik-root .MuiTab-root,
      .bayilik-root .MuiChip-root {
        min-width: 0;
      }

      @media (max-width: ${MOBILE_BREAKPOINT}px) {
        html,
        body,
        #root {
          overflow-x: hidden;
        }
      }
    `;

    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchData = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const json = await apiJson(ENDPOINTS.dashboard);
        const normalized = normalizeDashboard(json);

        setPartner(normalized.partner);
        setRecords(normalized.records);
        setSummary(normalized.summary);
        setLastUpdated(new Date().toISOString());
      } catch (err) {
        console.error("Bayilik bilgileri alınamadı:", err);
        setError(err.message || TT.errorText);
        setRecords([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [TT.errorText]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const filteredRecords = useMemo(() => {
    const q = safeLower(search);

    if (!q) return records;

    return records.filter((row) => {
      const text = safeLower(
        [
          row.referans,
          row.firma_adi,
          row.satilan_urun,
          row.islem_tipi,
          row.anlasma_notu,
          row.musteri_adi,
          row.musteri_email,
          row.musteri_code,
          row.kurum_id,
          row.kurum_old_id,
        ].join(" ")
      );

      return text.includes(q);
    });
  }, [records, search]);

 const totals = useMemo(() => {
  return records.reduce(
    (acc, row) => {
      acc.totalSales += parseNumber(row.satis_tutari);
      acc.totalCommission += getCommissionAmount(row);

      if (isCurrentMonthSale(row)) {
        acc.currentMonthSalesCount += 1;
      }

      const days = getDaysLeft(row.sozlesme_bitis);

      if (days !== null && days >= 0) {
        acc.activeContracts += 1;
      }

      if (days !== null && days >= 0 && days <= 45) {
        acc.upcomingRenewals += 1;
      }

      return acc;
    },
    {
      totalSales: 0,
      totalCommission: 0,
      activeContracts: 0,
      upcomingRenewals: 0,
      currentMonthSalesCount: 0,
    }
  );
}, [records]);

 const stats = {
  totalRecords: summary.toplam_islem || records.length,
  activeContracts: summary.aktif_sozlesme || totals.activeContracts,
  upcomingRenewals: summary.yaklasan_sozlesme || totals.upcomingRenewals,
  currentMonthSalesCount: totals.currentMonthSalesCount,
  currentMonthSalesLabel: getMonthSalesLabel(lang),
  totalSales: totals.totalSales,
  totalCommission: totals.totalCommission,
};

  const reportData = useMemo(() => {
    return {
      byCustomer: buildReportRows(
        filteredRecords,
        (row) => getBuyerName(row, TT) || TT.customerMissing,
        (row) =>
          [
            row.musteri_email,
            row.musteri_code ? `Kod: ${row.musteri_code}` : "",
          ]
            .filter(Boolean)
            .join(" · "),
        TT
      ),
      byProduct: buildReportRows(
        filteredRecords,
        (row) => getProductName(row, TT) || TT.productMissing,
        (row) => getProcessTypeText(row.islem_tipi, TT),
        TT
      ),
    };
  }, [filteredRecords, TT]);

  const handleExport = () => {
    downloadCsv(
      `bayilik-bilgi-kayitlari-${new Date().toISOString().slice(0, 10)}.csv`,
      buildCsvRows(filteredRecords, lang, TT)
    );
  };

  if (loading) {
    return (
      <PageFrame>
        <LoadingView TT={TT} />
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <HeroSection
        TT={TT}
        lang={lang}
        setLang={setLang}
        partner={partner}
        refreshing={refreshing}
        lastUpdated={lastUpdated}
        onRefresh={() => fetchData(true)}
        onExport={handleExport}
        cardMode={cardMode}
      />

      {error && <ErrorCard TT={TT} text={error} />}

      <StatsGrid TT={TT} lang={lang} stats={stats} />

      <ReportsAccordion
        TT={TT}
        lang={lang}
        records={filteredRecords}
        reportData={reportData}
      />

      <SalesAccordion TT={TT} total={filteredRecords.length}>
        <RecordsHeader
          TT={TT}
          search={search}
          setSearch={setSearch}
          total={filteredRecords.length}
          cardMode={cardMode}
        />

        {!filteredRecords.length ? (
          <EmptyState TT={TT} />
        ) : (
          <Stack spacing={{ xs: 1.1, md: 1.6 }}>
            {filteredRecords.map((row) => (
              <RecordCard key={row.id} row={row} TT={TT} lang={lang} />
            ))}
          </Stack>
        )}
      </SalesAccordion>
    </PageFrame>
  );
}

function PageFrame({ children }) {
  return (
    <Box
      className="bayilik-root"
      sx={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100vw",
        minWidth: 0,
        overflowX: "hidden",
        p: {
          xs: 1,
          sm: 1.2,
          md: 0.8,
        },
        pb: {
          xs: 5,
          md: 7,
        },
        background:
          "radial-gradient(circle at top left, rgba(21,101,192,.12), transparent 32%), radial-gradient(circle at top right, rgba(245,130,32,.13), transparent 30%), linear-gradient(145deg, #F8FAFC 0%, #EEF2F7 100%)",
        color: BRAND.dark,

        "& *": {
          boxSizing: "border-box",
        },

        "& .MuiCard-root, & .MuiPaper-root": {
          maxWidth: "100%",
          minWidth: 0,
        },

        "& .MuiButton-root": {
          minWidth: 0,
        },

        "& .MuiStack-root": {
          minWidth: 0,
        },

        "& .MuiTypography-root": {
          maxWidth: "100%",
        },

        [`@media (min-width:${MOBILE_BREAKPOINT + 1}px)`]: {
          width: "calc(100vw - 230px)",
          maxWidth: "calc(100vw - 230px)",
          marginLeft: "50%",
          transform: "translateX(calc(-50vw + 115px))",
        },

        [`@media (max-width:${MOBILE_BREAKPOINT}px)`]: {
          p: 1,
          overflowX: "hidden",
          transform: "none",
          width: "100%",
          maxWidth: "100%",
          marginLeft: 0,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          mx: 0,
          overflowX: "hidden",
          animation: `${fadeUp} .35s ease both`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function LoadingView({ TT }) {
  return (
    <Box
      sx={{
        minHeight: {
          xs: "70vh",
          md: "78vh",
        },
        display: "grid",
        placeItems: "center",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 720,
          borderRadius: {
            xs: 3,
            md: 5,
          },
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(15,23,42,.10)",
          border: `1px solid ${BRAND.border}`,
        }}
      >
        <CardContent
          sx={{
            p: {
              xs: 2,
              md: 4,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
          >
            <Avatar
              sx={{
                width: 58,
                height: 58,
                bgcolor: BRAND.softBlue,
                color: BRAND.blue,
              }}
            >
              <HandshakeIcon />
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 950,
                  fontSize: {
                    xs: 22,
                    md: 30,
                  },
                  lineHeight: 1.1,
                  wordBreak: "break-word",
                }}
              >
                {TT.loadingTitle}
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  color: BRAND.muted,
                  fontWeight: 700,
                  fontSize: {
                    xs: 13,
                    md: 15,
                  },
                  lineHeight: 1.55,
                }}
              >
                {TT.loadingText}
              </Typography>
            </Box>

            <CircularProgress
              size={34}
              thickness={5}
              sx={{
                color: BRAND.orange,
                display: {
                  xs: "none",
                  sm: "block",
                },
              }}
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function HeroSection({
  TT,
  lang,
  setLang,
  partner,
  refreshing,
  lastUpdated,
  onRefresh,
  onExport,
  cardMode,
}) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: {
          xs: 1.2,
          md: 1.6,
        },
        borderRadius: {
          xs: 3,
          md: 4,
        },
        overflow: "hidden",
        position: "relative",
        color: "#fff",
        border: "1px solid rgba(255,255,255,.18)",
        background:
          "linear-gradient(135deg, rgba(15,23,42,.98), rgba(11,77,139,.96) 52%, rgba(245,130,32,.92))",
        boxShadow: {
          xs: "0 16px 40px rgba(15,23,42,.18)",
          md: "0 22px 54px rgba(15,23,42,.18)",
        },
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.3,
          background:
            "linear-gradient(110deg, transparent 0%, rgba(255,255,255,.16) 40%, transparent 70%)",
          backgroundSize: "180% 100%",
          animation: `${shine} 4.8s linear infinite`,
        }}
      />

      <CardContent
        sx={{
          position: "relative",
          zIndex: 1,
          p: {
            xs: 1.4,
            sm: 2,
            md: 2.4,
          },
          minWidth: 0,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={{
            xs: 1.4,
            md: 2,
          }}
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{
              minWidth: 0,
              flex: 1,
              order: {
                xs: 2,
                md: 1,
              },
            }}
          >
            <Avatar
              sx={{
                width: {
                  xs: 58,
                  md: 66,
                },
                height: {
                  xs: 58,
                  md: 66,
                },
                bgcolor: "rgba(255,255,255,.14)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,.22)",
                boxShadow: "0 18px 40px rgba(0,0,0,.20)",
                animation: `${floatSoft} 4s ease-in-out infinite`,
                flexShrink: 0,
              }}
            >
              <HandshakeIcon
                sx={{
                  fontSize: {
                    xs: 32,
                    md: 36,
                  },
                }}
              />
            </Avatar>

            <Box
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              {(partner?.code || partner?.email) && (
                <Chip
                  size="small"
                  label={partner?.code || partner?.email}
                  sx={{
                    mb: 0.8,
                    bgcolor: "rgba(255,255,255,.12)",
                    color: "#FFEDD5",
                    border: "1px solid rgba(255,255,255,.16)",
                    fontWeight: 900,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              )}

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 950,
                  letterSpacing: "-.045em",
                  lineHeight: 1.05,
                  fontSize: {
                    xs: 32,
                    sm: 36,
                    md: 42,
                  },
                  wordBreak: "break-word",
                }}
              >
                {TT.pageTitle}
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  maxWidth: 880,
                  color: "#DBEAFE",
                  fontWeight: 650,
                  fontSize: {
                    xs: 16,
                    md: 15,
                  },
                  lineHeight: {
                    xs: 1.55,
                    md: 1.45,
                  },
                  wordBreak: "break-word",
                }}
              >
                {TT.pageSubtitle}
              </Typography>
            </Box>
          </Stack>

          <Stack
            spacing={0.9}
            alignItems={{
              xs: "flex-start",
              md: "flex-end",
            }}
            sx={{
              minWidth: 0,
              width: {
                xs: "100%",
                md: "auto",
              },
              order: {
                xs: 1,
                md: 2,
              },
            }}
          >
            <Stack
              direction="row"
              spacing={0.7}
              justifyContent="flex-start"
              sx={{
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={onRefresh}
                disabled={refreshing}
                startIcon={
                  refreshing ? (
                    <CircularProgress size={13} sx={{ color: "#fff" }} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  minHeight: {
                    xs: 34,
                    md: 32,
                  },
                  px: 1.15,
                  borderRadius: 2.2,
                  fontSize: 12,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,.14)",
                  border: "1px solid rgba(255,255,255,.14)",
                  textTransform: "none",
                  fontWeight: 900,
                  width: "auto",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,.22)",
                  },
                }}
              >
                {refreshing ? TT.refreshing : TT.refresh}
              </Button>

              <Button
                onClick={onExport}
                startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: {
                    xs: 34,
                    md: 32,
                  },
                  px: 1.15,
                  borderRadius: 2.2,
                  fontSize: 12,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,.14)",
                  border: "1px solid rgba(255,255,255,.14)",
                  textTransform: "none",
                  fontWeight: 900,
                  width: "auto",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,.22)",
                  },
                }}
              >
                {TT.exportCsv}
              </Button>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={{
                xs: "flex-start",
                md: "flex-end",
              }}
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            >
              <Select
                size="small"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                sx={{
                  ...selectSx,
                  minWidth: cardMode ? 106 : 118,
                  minHeight: {
                    xs: 38,
                    md: 34,
                  },
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,.12)",
                  borderRadius: 2.5,
                  "& fieldset": {
                    borderColor: "rgba(255,255,255,.20)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                }}
                startAdornment={
                  <LanguageIcon
                    sx={{
                      mr: 0.7,
                      fontSize: 18,
                      color: "#fff",
                    }}
                  />
                }
              >
                <MenuItem value="tr">TR</MenuItem>
                <MenuItem value="en">EN</MenuItem>
                <MenuItem value="de">DE</MenuItem>
              </Select>

              <Typography
                sx={{
                  color: "#DBEAFE",
                  fontWeight: 750,
                  fontSize: 11.5,
                  textAlign: "right",
                  display: {
                    xs: "none",
                    sm: "block",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 210,
                }}
              >
                {TT.lastUpdate}:{" "}
                {lastUpdated ? dateTimeText(lastUpdated, lang) : TT.never}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ErrorCard({ TT, text }) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.4,
        borderRadius: 3,
        border: "1px solid rgba(239,68,68,.24)",
        bgcolor: BRAND.softRed,
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 1.4,
            md: 1.7,
          },
          "&:last-child": {
            pb: {
              xs: 1.4,
              md: 1.7,
            },
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: 950,
            color: BRAND.danger,
            mb: 0.35,
          }}
        >
          {TT.errorTitle}
        </Typography>

        <Typography
          sx={{
            color: BRAND.muted,
            fontWeight: 750,
            fontSize: 13,
          }}
        >
          {text || TT.errorText}
        </Typography>
      </CardContent>
    </Card>
  );
}

function StatsGrid({ TT, lang, stats }) {
  const items = [
    {
      icon: <BusinessIcon />,
      label: TT.totalRecords,
      value: stats.totalRecords,
      bg: BRAND.softBlue,
      color: BRAND.blue,
    },
    {
      icon: <FactCheckIcon />,
      label: TT.activeContracts,
      value: stats.activeContracts,
      bg: BRAND.softGreen,
      color: "#047857",
    },
   {
  icon: <EventRepeatIcon />,
  label: TT.upcomingRenewals,
  value: stats.upcomingRenewals,
  bg: BRAND.softYellow,
  color: "#B45309",
},
{
  icon: <PointOfSaleIcon />,
  label: stats.currentMonthSalesLabel,
  value: stats.currentMonthSalesCount,
  bg: "#F0FDF4",
  color: "#15803D",
},
{
  icon: <ReceiptLongIcon />,
  label: TT.totalSalesAmount,
  value: moneyTextShort(stats.totalSales, "TRY", lang),
  bg: BRAND.softOrange,
  color: "#9A3412",
  wideMobile: true,
},
{
  icon: <HandshakeIcon />,
  label: TT.infoCommission,
  value: moneyTextShort(stats.totalCommission, "TRY", lang),
  bg: "#EEF2FF",
  color: "#3730A3",
  wideMobile: true,
},
  ];

  return (
    <Box
      sx={{
        display: "grid",
       gridTemplateColumns: {
  xs: "repeat(2, minmax(0, 1fr))",
  md: "repeat(6, minmax(0, 1fr))",
},
        gap: {
          xs: 1,
          md: 1.2,
        },
        mb: {
          xs: 1.2,
          md: 1.5,
        },
        minWidth: 0,
      }}
    >
      {items.map((item) => (
        <Card
          key={item.label}
          elevation={0}
          sx={{
            gridColumn: {
              xs: item.wideMobile ? "1 / -1" : "auto",
              md: "auto",
            },
            borderRadius: {
              xs: 3,
              md: 3.5,
            },
            border: `1px solid ${BRAND.border}`,
            bgcolor: "#fff",
            boxShadow: "0 12px 30px rgba(15,23,42,.06)",
            minWidth: 0,
          }}
        >
          <CardContent
            sx={{
              p: {
                xs: 1.25,
                md: 1.5,
              },
              "&:last-child": {
                pb: {
                  xs: 1.25,
                  md: 1.5,
                },
              },
            }}
          >
            <Stack direction="row" spacing={1.1} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: item.bg,
                  color: item.color,
                  width: {
                    xs: 42,
                    md: 42,
                  },
                  height: {
                    xs: 42,
                    md: 42,
                  },
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    color: BRAND.muted,
                    fontWeight: 850,
                    fontSize: {
                      xs: 12,
                      md: 12,
                    },
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {item.label}
                </Typography>

                <Typography
                  sx={{
                    color: BRAND.dark,
                    fontWeight: 950,
                    fontSize: {
                      xs: 22,
                      md: 20,
                    },
                    lineHeight: 1.1,
                    mt: 0.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

function ReportsAccordion({ TT, lang, records, reportData }) {
  const totalSales = records.reduce(
    (sum, row) => sum + parseNumber(row.satis_tutari),
    0
  );

  const totalCommission = records.reduce(
    (sum, row) => sum + getCommissionAmount(row),
    0
  );

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        mb: {
          xs: 1.2,
          md: 1.5,
        },
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        overflow: "hidden",
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 14px 36px rgba(15,23,42,.06)",
        bgcolor: "#fff",
        "&:before": {
          display: "none",
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
          width: 42,
          height: 42,
          borderRadius: 999,
          display: "grid",
          placeItems: "center",
          bgcolor: BRAND.softBlue,
          color: BRAND.blue,
          transition: "all .25s ease",
        },
        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
          transform: "rotate(180deg) scale(1.08)",
          bgcolor: BRAND.softOrange,
          color: "#9A3412",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: 34 }} />}
        sx={{
          px: {
            xs: 1.3,
            md: 1.8,
          },
          py: {
            xs: 0.6,
            md: 0.8,
          },
          minHeight: "unset",
          "& .MuiAccordionSummary-content": {
            my: 0.7,
            minWidth: 0,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.1}
          alignItems="center"
          sx={{ minWidth: 0, width: "100%" }}
        >
          <Avatar
            sx={{
              bgcolor: BRAND.softOrange,
              color: "#9A3412",
              width: 42,
              height: 42,
              flexShrink: 0,
            }}
          >
            <ReceiptLongIcon />
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 950,
                color: BRAND.dark,
                fontSize: {
                  xs: 20,
                  md: 22,
                },
                lineHeight: 1.1,
                wordBreak: "break-word",
              }}
            >
              {TT.reportsTitle}
            </Typography>

            <Typography
              sx={{
                color: BRAND.muted,
                fontWeight: 750,
                fontSize: 13,
                mt: 0.25,
                wordBreak: "break-word",
              }}
            >
              {TT.reportsSubtitle}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.7}
            sx={{
              display: {
                xs: "none",
                md: "flex",
              },
            }}
          >
            <Chip
              size="small"
              label={`${records.length} ${TT.rows}`}
              sx={{
                bgcolor: BRAND.softBlue,
                color: BRAND.blue,
                fontWeight: 950,
              }}
            />

            <Chip
              size="small"
              label={moneyText(totalSales, "TRY", lang)}
              sx={{
                bgcolor: BRAND.softOrange,
                color: "#9A3412",
                fontWeight: 950,
              }}
            />

            <Chip
              size="small"
              label={moneyText(totalCommission, "TRY", lang)}
              sx={{
                bgcolor: "#EEF2FF",
                color: "#3730A3",
                fontWeight: 950,
              }}
            />
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          p: {
            xs: 1.1,
            md: 1.4,
          },
          pt: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "1fr 1fr",
            },
            gap: {
              xs: 1.1,
              md: 1.3,
            },
            minWidth: 0,
          }}
        >
          <ReportTable
            title={TT.customerReport}
            subtitle={TT.customerReportSub}
            emptyText={TT.noReport}
            rows={reportData.byCustomer}
            lang={lang}
            mode="customer"
            TT={TT}
          />

          <ReportTable
            title={TT.productReport}
            subtitle={TT.productReportSub}
            emptyText={TT.noReport}
            rows={reportData.byProduct}
            lang={lang}
            mode="product"
            TT={TT}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function ReportTable({ title, subtitle, emptyText, rows, lang, mode, TT }) {
  const palette = [
    {
      bg: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
      border: "#93C5FD",
      color: "#1D4ED8",
    },
    {
      bg: "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)",
      border: "#FDBA74",
      color: "#C2410C",
    },
    {
      bg: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
      border: "#6EE7B7",
      color: "#047857",
    },
    {
      bg: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
      border: "#C4B5FD",
      color: "#6D28D9",
    },
    {
      bg: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)",
      border: "#FCA5A5",
      color: "#B91C1C",
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3.5,
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        overflow: "hidden",
        minWidth: 0,
        height: "100%",
      }}
    >
      <Box
        sx={{
          p: {
            xs: 1.2,
            md: 1.4,
          },
          background:
            mode === "customer"
              ? "linear-gradient(135deg, #0B4D8B 0%, #1565C0 100%)"
              : "linear-gradient(135deg, #F58220 0%, #FB923C 100%)",
          color: "#fff",
        }}
      >
        <Typography
          sx={{
            fontWeight: 950,
            fontSize: {
              xs: 17,
              md: 18,
            },
            lineHeight: 1.1,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt: 0.35,
            color: "rgba(255,255,255,.84)",
            fontWeight: 700,
            fontSize: 12.5,
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      <Box
        sx={{
          p: {
            xs: 1,
            md: 1.2,
          },
        }}
      >
        <Box
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1.2fr) 80px 140px 140px",
            },
            gap: 0.8,
            px: 0.7,
            py: 0.6,
            color: BRAND.muted,
            fontWeight: 950,
            fontSize: 11.5,
            textTransform: "uppercase",
            letterSpacing: ".04em",
            display: {
              xs: "none",
              md: "grid",
            },
          }}
        >
          <Box>{mode === "customer" ? TT.customerAccount : TT.packageName}</Box>
          <Box textAlign="center">{TT.quantity}</Box>
          <Box textAlign="right">{TT.saleAmount}</Box>
          <Box textAlign="right">{TT.commissionAmount}</Box>
        </Box>

        {!rows?.length ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: "#F8FAFC",
              border: `1px dashed ${BRAND.border2}`,
              color: BRAND.muted,
              fontWeight: 800,
              textAlign: "center",
            }}
          >
            {emptyText}
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {rows.slice(0, 8).map((row, index) => {
              const c = palette[index % palette.length];

              return (
                <Box
                  key={`${row.key}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "minmax(0, 1.2fr) 80px 140px 140px",
                    },
                    gap: {
                      xs: 0.55,
                      md: 0.8,
                    },
                    alignItems: "center",
                    p: {
                      xs: 1,
                      md: 0.9,
                    },
                    borderRadius: 3,
                    background: c.bg,
                    border: `1px solid ${c.border}`,
                    minWidth: 0,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: "#fff",
                        color: c.color,
                        fontWeight: 950,
                        fontSize: 13,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: BRAND.dark,
                          fontWeight: 950,
                          lineHeight: 1.15,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.label}
                      </Typography>

                      {row.subText && row.subText !== "-" && (
                        <Typography
                          sx={{
                            color: BRAND.muted,
                            fontWeight: 750,
                            fontSize: 12,
                            mt: 0.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.subText}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  <MetricPill label={TT.quantity} value={row.count} color={c.color} />
                  <MetricPill
                    label={TT.saleAmount}
                    value={moneyText(row.saleAmount, "TRY", lang)}
                    color={c.color}
                    right
                  />
                  <MetricPill
                    label={TT.commissionAmount}
                    value={moneyText(row.commissionAmount, "TRY", lang)}
                    color={c.color}
                    right
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Card>
  );
}

function MetricPill({ label, value, color, right }) {
  return (
    <Box
      sx={{
        textAlign: {
          xs: "left",
          md: right ? "right" : "center",
        },
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          display: {
            xs: "block",
            md: "none",
          },
          color: BRAND.muted,
          fontWeight: 850,
          fontSize: 11,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color,
          fontWeight: 950,
          fontSize: {
            xs: 13,
            md: 13.5,
          },
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SalesAccordion({ TT, total, children }) {
  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        overflow: "hidden",
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 14px 36px rgba(15,23,42,.06)",
        bgcolor: "#fff",
        "&:before": {
          display: "none",
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
          width: 42,
          height: 42,
          borderRadius: 999,
          display: "grid",
          placeItems: "center",
          bgcolor: BRAND.softOrange,
          color: "#9A3412",
          transition: "all .25s ease",
        },
        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
          transform: "rotate(180deg) scale(1.08)",
          bgcolor: BRAND.softBlue,
          color: BRAND.blue,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: 34 }} />}
        sx={{
          px: {
            xs: 1.4,
            md: 1.8,
          },
          py: {
            xs: 0.6,
            md: 0.8,
          },
          minHeight: "unset",
          borderBottom: `1px solid ${BRAND.border}`,
          background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
          "& .MuiAccordionSummary-content": {
            my: 0.7,
            minWidth: 0,
          },
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              bgcolor: BRAND.softBlue,
              color: BRAND.blue,
              width: 42,
              height: 42,
              flexShrink: 0,
            }}
          >
            <HandshakeIcon />
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 950,
                color: BRAND.dark,
                fontSize: {
                  xs: 20,
                  md: 22,
                },
                lineHeight: 1.1,
              }}
            >
              {TT.sales}
            </Typography>

            <Typography
              sx={{
                color: BRAND.muted,
                fontWeight: 750,
                fontSize: 13,
                mt: 0.25,
              }}
            >
              {total} {TT.salesListed}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          p: {
            xs: 1,
            md: 1.3,
          },
          bgcolor: "#F8FAFC",
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function RecordsHeader({ TT, search, setSearch, total, cardMode }) {
  return (
    <Card
      elevation={0}
      sx={{
        mb: {
          xs: 1.1,
          md: 1.3,
        },
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        boxShadow: "0 12px 30px rgba(15,23,42,.05)",
        minWidth: 0,
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 1.3,
            md: 1.6,
          },
          "&:last-child": {
            pb: {
              xs: 1.3,
              md: 1.6,
            },
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={1.2}
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography
                sx={{
                  fontSize: {
                    xs: 18,
                    md: 21,
                  },
                  fontWeight: 950,
                  color: BRAND.dark,
                  lineHeight: 1.15,
                  wordBreak: "break-word",
                }}
              >
                {TT.recordsTitle}
              </Typography>

              <Chip
                size="small"
                label={`${total} ${TT.rows}`}
                sx={{
                  bgcolor: BRAND.softBlue,
                  color: BRAND.blue,
                  fontWeight: 950,
                }}
              />
            </Stack>

            <Typography
              sx={{
                mt: 0.5,
                color: BRAND.muted,
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {TT.recordsSubtitle}
            </Typography>
          </Box>

          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={TT.searchPlaceholder}
            size="small"
            fullWidth={cardMode}
            sx={{
              ...fieldSx,
              width: {
                xs: "100%",
                md: 390,
              },
              flexShrink: 0,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function EmptyState({ TT }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px dashed ${BRAND.border2}`,
        bgcolor: "#fff",
        minWidth: 0,
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 3,
            md: 5,
          },
          textAlign: "center",
        }}
      >
        <Avatar
          sx={{
            width: 66,
            height: 66,
            mx: "auto",
            mb: 1.5,
            bgcolor: BRAND.softBlue,
            color: BRAND.blue,
          }}
        >
          <BusinessIcon />
        </Avatar>

        <Typography
          sx={{
            fontWeight: 950,
            fontSize: {
              xs: 20,
              md: 24,
            },
            color: BRAND.dark,
          }}
        >
          {TT.noData}
        </Typography>

        <Typography
          sx={{
            color: BRAND.muted,
            fontWeight: 700,
            mt: 0.7,
            lineHeight: 1.5,
          }}
        >
          {TT.noDataText}
        </Typography>
      </CardContent>
    </Card>
  );
}

function RecordCard({ row, TT, lang }) {
  const chip = getRemainingChip(row.sozlesme_bitis, TT);
  const commissionAmount = getCommissionAmount(row);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: {
          xs: 3,
          md: 3.5,
        },
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        boxShadow: "0 12px 32px rgba(15,23,42,.06)",
        overflow: "hidden",
        minWidth: 0,
        width: "100%",
      }}
    >
      <CardContent
        sx={{
          p: {
            xs: 1.25,
            sm: 1.6,
            md: 1.8,
          },
          "&:last-child": {
            pb: {
              xs: 1.25,
              sm: 1.6,
              md: 1.8,
            },
          },
          minWidth: 0,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={{
            xs: 1.2,
            md: 1.6,
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            md: "flex-start",
          }}
          sx={{
            minWidth: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1.2}
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Avatar
              sx={{
                bgcolor: alpha(BRAND.blue, 0.08),
                color: BRAND.blue,
                width: {
                  xs: 44,
                  md: 52,
                },
                height: {
                  xs: 44,
                  md: 52,
                },
                fontWeight: 950,
                flexShrink: 0,
              }}
            >
              {safeText(row.firma_adi, "?").charAt(0).toUpperCase()}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                flexWrap="wrap"
                sx={{
                  mb: 0.4,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 950,
                    color: BRAND.dark,
                    fontSize: {
                      xs: 17,
                      md: 20,
                    },
                    lineHeight: 1.18,
                    wordBreak: "break-word",
                    minWidth: 0,
                  }}
                >
                  {safeText(row.firma_adi, TT.company)}
                </Typography>

                <Chip
                  size="small"
                  label={getProcessTypeText(row.islem_tipi, TT)}
                  sx={{
                    bgcolor: BRAND.softBlue,
                    color: BRAND.blue,
                    fontWeight: 950,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />

                <Chip
                  size="small"
                  label={chip.label}
                  sx={{
                    bgcolor: chip.bg,
                    color: chip.color,
                    fontWeight: 950,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Stack>

              <Typography
                sx={{
                  color: BRAND.muted,
                  fontWeight: 750,
                  fontSize: {
                    xs: 13,
                    md: 14,
                  },
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
              >
                {TT.partner}: <b>{safeText(row.referans)}</b>
              </Typography>

              <Typography
                sx={{
                  color: BRAND.muted,
                  fontWeight: 750,
                  fontSize: {
                    xs: 13,
                    md: 14,
                  },
                  lineHeight: 1.45,
                  wordBreak: "break-word",
                }}
              >
                {TT.packageName}: <b>{safeText(row.satilan_urun)}</b>
              </Typography>

              {(row.musteri_email || row.musteri_code || row.musteri_adi) && (
                <Typography
                  sx={{
                    color: BRAND.muted,
                    fontWeight: 750,
                    fontSize: {
                      xs: 13,
                      md: 14,
                    },
                    lineHeight: 1.45,
                    wordBreak: "break-word",
                  }}
                >
                  {TT.customerAccount}:{" "}
                  <b>
                    {safeText(row.musteri_adi || row.firma_adi)}
                    {row.musteri_email ? ` · ${row.musteri_email}` : ""}
                    {row.musteri_code ? ` · Kod: ${row.musteri_code}` : ""}
                  </b>
                </Typography>
              )}
            </Box>
          </Stack>

          <Box
            sx={{
              width: {
                xs: "100%",
                md: 330,
              },
              minWidth: 0,
              p: {
                xs: 1.1,
                md: 1.3,
              },
              borderRadius: 3,
              bgcolor: "#F8FAFC",
              border: `1px solid ${BRAND.border}`,
            }}
          >
            <Stack spacing={0.8}>
              <InfoLine
                label={TT.saleAmount}
                value={moneyText(row.satis_tutari, row.currency, lang)}
              />

              <InfoLine
                label={`${row.hakedis_kademe || "1"}. ${TT.commissionStep}`}
                value={percentText(row.hakedis_orani, lang)}
              />

              <InfoLine
                label={TT.commissionAmount}
                value={moneyText(commissionAmount, row.currency, lang)}
                strong
              />
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: { xs: 1.2, md: 1.4 } }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: {
              xs: 0.9,
              md: 1,
            },
            minWidth: 0,
          }}
        >
          <MiniBox
            label={TT.agreementDate}
            value={dateText(row.anlasma_tarihi, lang)}
          />

          <MiniBox
            label={TT.startDate}
            value={dateText(row.sozlesme_baslangic, lang)}
          />

          <MiniBox
            label={TT.endDate}
            value={dateText(row.sozlesme_bitis, lang)}
          />

          <MiniBox label={TT.remainingDay} value={chip.label} />
        </Box>

        <Box
          sx={{
            mt: 1.2,
            p: 1.2,
            borderRadius: 3,
            bgcolor: row.anlasma_notu ? BRAND.softOrange : "#F8FAFC",
            border: row.anlasma_notu
              ? "1px dashed rgba(245,130,32,.35)"
              : `1px dashed ${BRAND.border2}`,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 950,
              color: row.anlasma_notu ? "#9A3412" : BRAND.muted,
              mb: 0.3,
            }}
          >
            {TT.note}
          </Typography>

          <Typography
            sx={{
              color: BRAND.ink,
              fontWeight: 700,
              fontSize: {
                xs: 12.5,
                md: 13.5,
              },
              lineHeight: 1.55,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {safeText(row.anlasma_notu, TT.noNote)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function InfoLine({ label, value, strong = false }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={1}
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          color: BRAND.muted,
          fontWeight: 800,
          fontSize: 12,
          minWidth: 0,
          wordBreak: "break-word",
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: strong ? BRAND.blue : BRAND.dark,
          fontWeight: 950,
          fontSize: {
            xs: 12.5,
            md: 13.5,
          },
          textAlign: "right",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "58%",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function MiniBox({ label, value }) {
  return (
    <Box
      sx={{
        p: {
          xs: 1,
          md: 1.1,
        },
        borderRadius: 3,
        bgcolor: "#F8FAFC",
        border: `1px solid ${BRAND.border}`,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          color: BRAND.muted,
          fontWeight: 850,
          fontSize: 12,
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: BRAND.dark,
          fontWeight: 950,
          mt: 0.35,
          fontSize: {
            xs: 13,
            md: 14,
          },
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}